import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  PlayArrow as TestIcon,
  VpnKey as KeyIcon,
  DateRange as DateIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const CACHE_KEY = "sellerlogic_credentials_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
import { sellerlogicApi } from "../../../services/api/multi/sellerlogic";

const SellerlogicCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    display_name: "",
    client_id: "",
    client_secret: "",
    active_multi: false,
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    // Check cache first
    if (!forceRefresh) {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.timestamp < CACHE_DURATION) {
            setCredentials(parsed.data);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Cache parse error", e);
        }
      }
    }

    try {
      const response = await sellerlogicApi.getCredentials();
      const creds = response.data || [];
      setCredentials(creds);
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: creds,
          timestamp: Date.now(),
        }),
      );
    } catch (err) {
      console.error("Failed to load Sellerlogic credentials:", err);
      setError("Failed to load credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (credential = null) => {
    if (credential) {
      setEditingId(credential.id);
      setFormData({
        display_name: credential.display_name || "",
        client_id: credential.client_id || "",
        client_secret: "", // Don't populate secret
        active_multi: credential.active_multi || false,
      });
    } else {
      setEditingId(null);
      setFormData({
        display_name: "",
        client_id: "",
        client_secret: "",
        active_multi: false,
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.client_id) {
      setError("Client ID is required.");
      return;
    }
    // Secret is required for new, optional for edit
    if (!editingId && !formData.client_secret) {
      setError("Client Secret is required for new credentials.");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Only send fields that have values (handle partial update logic if needed)
        const dataToSend = { ...formData };
        if (!dataToSend.client_secret) {
          delete dataToSend.client_secret;
        }
        await sellerlogicApi.updateCredential(editingId, dataToSend);
        setSuccess("Credential updated successfully.");
      } else {
        await sellerlogicApi.createCredential(formData);
        setSuccess("Credential created successfully.");
      }
      handleCloseDialog();
      loadCredentials(true);
    } catch (err) {
      console.error("Error saving credential:", err);
      setError(err.response?.data?.detail || "Failed to save credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?"))
      return;

    setLoading(true);
    try {
      await sellerlogicApi.deleteCredential(id);
      setSuccess("Credential deleted successfully.");
      loadCredentials(true);
    } catch (err) {
      console.error("Error deleting credential:", err);
      setError("Failed to delete credential.");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async (id) => {
    setLoading(true);
    try {
      const response = await sellerlogicApi.testCredential(id);
      if (
        response.data?.status === "success" ||
        response.data?.valid === true
      ) {
        setSuccess("Connection successful! Credential is valid.");
      } else {
        setError(
          `Connection failed: ${
            response.data?.detail || "Invalid credentials"
          }`,
        );
      }
    } catch (err) {
      console.error("Test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTestKey = async () => {
    if (!formData.client_id || !formData.client_secret) {
      setError("Please enter Client ID and Client Secret to test.");
      return;
    }

    setLoading(true);
    try {
      const response = await sellerlogicApi.testCredentialKey({
        client_id: formData.client_id,
        client_secret: formData.client_secret,
      });

      if (
        response.data?.status === "success" ||
        response.data?.valid === true
      ) {
        setSuccess("Connection successful! Keys are valid.");
      } else {
        setError(
          `Connection failed: ${response.data?.detail || "Invalid keys"}`,
        );
      }
    } catch (err) {
      console.error("Key test failed:", err);
      setError("Connection test failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    // Optimistic UI update could be risky if API fails, so we wait.
    try {
      await sellerlogicApi.toggleActiveCredential(id);
      loadCredentials(true); // Reload list to update other items if exclusivity exists
    } catch (err) {
      console.error("Error toggling active status:", err);
      setError("Failed to toggle active status.");
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={cp.outerCard}>
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              sx={{
                ...cp.header,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "auto",
                  paddingRight: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  🔐 Sellerlogic Credentials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage your Sellerlogic integration access
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => loadCredentials(true)}
                  disabled={loading}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>How to Get Your Sellerlogic Credentials:</strong>
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Login to Sellerlogic platform</li>
                <li>Confirm 2FA</li>
                <li>
                  Obtain your Client ID and Client Secret from application
                  details
                </li>
                <li>Enter the credentials in the form below</li>
              </ol>
              <Typography variant="body2" mt={1}>
                For more information, visit{" "}
                <a
                  href="https://sellerlogic.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  SELLERLOGIC API Access
                </a>
              </Typography>
            </Alert>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setSuccess(null)}
              >
                {success}
              </Alert>
            )}

            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress size={60} thickness={4} />
              </Box>
            ) : credentials.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No Sellerlogic credentials found. Add one to get started.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {credentials.map((cred, index) => (
                  <Grid item xs={12} key={cred.id}>
                    <Card
                      sx={{
                        background: "white",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.2s ease",
                        borderRadius:
                          index === 0
                            ? "8px 8px 0 0"
                            : index === credentials.length - 1
                              ? "0 0 8px 8px"
                              : 0,
                      }}
                    >
                      <CardContent>
                        <Grid container alignItems="center" spacing={3}>
                          <Grid item xs={12} md={8}>
                            <Typography variant="h6">
                              {cred.display_name || "Unnamed"}
                            </Typography>
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap={0.5}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  wordBreak: "break-all",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <KeyIcon fontSize="small" color="action" />{" "}
                                Client ID:{" "}
                                <span style={{ fontFamily: "monospace" }}>
                                  {cred.client_id
                                    ? `${cred.client_id.substring(0, 12)}...`
                                    : "N/A"}
                                </span>
                              </Typography>
                              {cred.created_at && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <DateIcon fontSize="small" color="action" />{" "}
                                  Created:{" "}
                                  {new Date(
                                    cred.created_at,
                                  ).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>

                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              {cred.active_multi && (
                                <Chip
                                  label="ACTIVE"
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  icon={<CheckCircleIcon />}
                                />
                              )}
                              {!cred.active_multi && (
                                <Chip
                                  label="Not Active"
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                  icon={<CancelIcon />}
                                />
                              )}
                            </Box>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                              display: "flex",
                              justifyContent: {
                                xs: "center",
                                md: "flex-end",
                              },
                              alignItems: "center",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                mr: 2,
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={cred.active_multi || false}
                                    onChange={() => handleToggleActive(cred.id)}
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "success.main",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "success.main",
                                        },
                                    }}
                                  />
                                }
                                label=""
                                sx={{ m: 0 }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: cred.active_multi
                                    ? "success.main"
                                    : "text.secondary",
                                  fontWeight: "bold",
                                  mt: 0.5,
                                }}
                              >
                                {cred.active_multi ? "Active" : "Inactive"}
                              </Typography>
                            </Box>

                            <IconButton
                              onClick={() => handleTest(cred.id)}
                              disabled={loading}
                              color="info"
                              title="Test Connection"
                              sx={{
                                backgroundColor: "info.main",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "info.dark",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              title="Edit Credential"
                              onClick={() => handleOpenDialog(cred)}
                              color="warning"
                              disabled={loading}
                              sx={{
                                backgroundColor: "warning.main",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "warning.dark",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              title="Delete Credential"
                              onClick={() => handleDelete(cred.id)}
                              color="error"
                              disabled={loading}
                              sx={{
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "error.dark",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? "Edit Sellerlogic Credential"
            : "Add Sellerlogic Credential"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Display Name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              placeholder="My Sellerlogic Account"
              helperText="A friendly name to identify this account"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Client ID"
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required={!editingId}
              fullWidth
              label="Client Secret"
              name="client_secret"
              type="password"
              value={formData.client_secret}
              onChange={handleChange}
              placeholder={editingId ? "Leave blank to keep unchanged" : ""}
              helperText={
                editingId
                  ? "Only enter if you want to update the secret"
                  : "Required for new accounts"
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestKey}
            color="info"
            disabled={loading || !formData.client_id || !formData.client_secret}
          >
            Test Keys
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SellerlogicCredentials;
